import { ListTypesAPI } from '@openx/types/inventoryList';
import type { AppInventoryOptionType } from '@openx/types/targeting/appInventory';
import type { InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { AnyAction } from '@reduxjs/toolkit';
import {
  type Dispatch,
  type KeyboardEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../../utils/context/TargetingContext';
import type { InputTypeValue } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants';
import { handleDuplicateErrorHandling } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/handleDuplicateErrorHandling';
import { processManualInput } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/processManualInput';
import type { InvalidItemsError } from '../../BulkAdd/types';
import { setComparisonOpValue } from '../inventoryHelpers';

export type UseAppBundleIdWithInvListStateProps = {
  comparisonType: ComparisonType;
  contentOption: InventoryContentOption | AppInventoryOptionType;
  inventoryContent: InventoryAndContentState[typeof InventoryContentOption.APP_BUNDLE_ID];
  accountUid?: string;
  dispatchInventory: (action: AnyAction) => void;
  setAppBundleIdDuplicateErrors: Dispatch<SetStateAction<InvalidItemsError | null>>;
};

export const useAppBundleIdWithInvListState = ({
  comparisonType,
  contentOption,
  inventoryContent,
  accountUid = '',
  dispatchInventory,
  setAppBundleIdDuplicateErrors,
}: UseAppBundleIdWithInvListStateProps) => {
  const { useInventoryListsOptionsFetch, useInventoryListByIdFetch } = useTargetingContext();
  const {
    inventoryListsOptions,
    loading: loadingOptions,
    mapById,
  } = useInventoryListsOptionsFetch(ListTypesAPI.BUNDLE, accountUid);
  const { inventoryList, loading: loadingInventoryList } = useInventoryListByIdFetch(inventoryContent.inventorylist_id);

  const [errorText, setErrorText] = useState<string>('');
  const [inputType, setInputType] = useState<InputTypeValue>('manually');
  const { t } = useTranslation();
  const inventoryIdRef = useRef<string | null | undefined>(inventoryContent.inventorylist_id);

  useEffect(() => {
    if (inventoryIdRef.current !== inventoryContent.inventorylist_id && !loadingInventoryList) {
      handleDuplicateErrorHandling({
        inventoryListValues: inventoryList?.values || [],
        inventoryContent,
        setAppBundleIdDuplicateErrors,
        dispatchInventory,
      });

      inventoryIdRef.current = inventoryContent.inventorylist_id;
    }
  }, [loadingInventoryList, inventoryList?.values, setAppBundleIdDuplicateErrors, dispatchInventory, inventoryContent]);

  const isManualInputValid = useCallback(
    (value: string, inventoryListItems: string[]) => {
      return processManualInput({
        value,
        comparisonType,
        contentOption,
        inventoryContentOptions: inventoryContent?.val?.[comparisonType] || [],
        inventoryListItems,
        setErrorText,
        dispatchInventory,
        t,
      });
    },
    [comparisonType, contentOption, inventoryContent, dispatchInventory, t]
  );

  const handleManualInput = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!comparisonType) {
        return;
      }

      const target = e.target as HTMLInputElement;

      if (e.key === 'Enter' && target.value) {
        setAppBundleIdDuplicateErrors(null);

        if (isManualInputValid(target.value, inventoryList?.values || [])) {
          target.value = '';
        }
      }
    },
    [comparisonType, isManualInputValid, inventoryList?.values, setAppBundleIdDuplicateErrors]
  );

  const handleInventoryListChange = useCallback(
    (_, value: string | null) => {
      dispatchInventory({
        payload: {
          comparisonType,
          inventorylist_id: value,
        },
        type: setComparisonOpValue.app_bundle_id,
      } as unknown as AnyAction);
    },
    [dispatchInventory, comparisonType]
  );

  return {
    errorText,
    inputType,
    inventoryListsOptions,
    mapById,
    loading: loadingOptions || loadingInventoryList,
    handleManualInput,
    handleInventoryListChange,
    setInputType,
  };
};
